import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Observable, tap } from 'rxjs';
import { AuthTokens, BaseAuthService } from 'shared-auth';
import { environment } from '../../../environments/environment';
import {
  ActivateAccountRequest,
  ActivateAccountResponse,
  CreateUserRequest,
  CreateUserResponse,
  User
} from '../interfaces';
import { CurrencyService } from './currency.service';
import { LocaleSettingsService } from './locale-settings.service';
import { StatusService } from './status.service';
import { WebSocketDispatcherService } from './websocket-dispatcher.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseAuthService<User> {
  constructor(
    http: HttpClient,
    private router: Router,
    private statusService: StatusService,
    private currencyService: CurrencyService,
    private localeSettingsService: LocaleSettingsService,
    private injector: Injector
  ) {
    super(http, `${environment.apiUrl || '/api'}/company/auth`);
    this.listenForForceLogout();
  }

  /**
   * React to the server kicking this session because the account signed in
   * elsewhere. Lazily resolved to avoid the circular dependency between
   * AuthService and the WebSocket service.
   */
  private listenForForceLogout(): void {
    try {
      const wsService = this.injector.get(WebSocketDispatcherService);
      wsService.onForceLogout().subscribe((data) => {
        this.clearTokens();
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('auth.session_revoked_summary'),
          detail: data?.message || this.translateService.instant('auth.session_revoked_detail'),
          life: 7000,
        });
        this.router.navigate(['/auth/login']);
      });
    } catch (e) {
      // WebSocket service may not be available (e.g. during tests)
    }
  }

  private get messageService(): MessageService {
    return this.injector.get(MessageService);
  }

  private get translateService(): TranslateService {
    return this.injector.get(TranslateService);
  }

  /**
   * Initialize service - load statuses if user is authenticated
   * Call this after app initialization to avoid circular dependency
   */
  public initialize(): void {
    if (this.isAuthenticated && !this.statusService.isLoaded()) {
      this.statusService.loadStatuses().subscribe({
        error: () => { /* ignored */ }
      });
      this.currencyService.loadCurrency();
      this.localeSettingsService.loadLocale();
    }
  }

  /**
   * Create new user (Admin only)
   * Створює користувача і відправляє email з тимчасовим паролем
   */
  createUser(data: CreateUserRequest): Observable<CreateUserResponse> {
    return this.http.post<CreateUserResponse>(`${environment.apiUrl || '/api'}/admin/users`, data);
  }

  /**
   * Activate account with temporary password.
   *
   * Company's activation flow is email+temporaryPassword based, an
   * incompatible shape with shared-auth's token-based base
   * ActivateAccountRequest (used by admin/executor/client) - so this is a
   * separate method rather than an `override` of the inherited one.
   */
  activateAccountWithTemporaryPassword(data: ActivateAccountRequest): Observable<ActivateAccountResponse> {
    return this.http.post<ActivateAccountResponse>(`${this.authEndpoint}/activate`, data).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.setAuthData(response.data.user, response.data.tokens);
        }
      })
    );
  }

  /** Load statuses/currency after login or activation succeeds. */
  protected override onAfterLogin(_user: User, _tokens: AuthTokens): void {
    this.statusService.loadStatuses().subscribe({
      error: () => { /* ignored */ }
    });
    this.currencyService.reloadCurrency();
    this.localeSettingsService.reloadLocale();
  }

  /** Disconnect the socket and drop cached status/currency data before auth state clears. */
  protected override onBeforeLogout(): void {
    this.disconnectWebSocket();
    this.statusService.clearCache();
    this.currencyService.clearCache();
    this.localeSettingsService.clearCache();
  }

  override logout(): Observable<boolean> {
    return super.logout().pipe(tap(() => this.router.navigate(['/auth/login'])));
  }

  /**
   * Disconnect WebSocket connection (lazy inject to avoid circular dependency)
   */
  private disconnectWebSocket(): void {
    try {
      const wsService = this.injector.get(WebSocketDispatcherService);
      wsService.disconnect();
    } catch (e) {
      // WebSocket service may not be available
    }
  }

  // ============================================
  // Storage management overrides
  // ============================================

  /** Clear company-specific cached data before switching to a different company's user. */
  protected override setAuthData(user: User, tokens: AuthTokens): void {
    const currentUser = this.currentUserValue;
    if (currentUser && currentUser.companyId !== user.companyId) {
      this.clearCompanyData();
    }
    super.setAuthData(user, tokens);
  }

  protected override clearAuthData(): void {
    this.clearCompanyData();
    super.clearAuthData();
    // Always clear impersonation state on full auth clear (logout, session expiry)
    localStorage.removeItem(this.IMPERSONATION_KEY);
    localStorage.removeItem(this.ORIGINAL_TOKEN_KEY);
    localStorage.removeItem('original_refresh_token');
    localStorage.removeItem('original_user');
  }

  /**
   * Clear company-specific localStorage data to prevent data leakage between companies
   */
  private clearCompanyData(): void {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('company-')) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  // ============================================
  // Impersonation (Quick Login as fleet company)
  // ============================================

  private readonly IMPERSONATION_KEY = 'impersonation_data';
  private readonly ORIGINAL_TOKEN_KEY = 'original_token';

  startImpersonation(data: { token: string; expiresAt: string; companyName: string; companyId: number; companyType?: string; originalCompanyId: number }): void {
    const originalToken = localStorage.getItem('access_token');
    const originalRefreshToken = localStorage.getItem('refresh_token');
    const originalUserJson = localStorage.getItem('currentUser');

    if (originalToken) {
      localStorage.setItem(this.ORIGINAL_TOKEN_KEY, originalToken);
    }
    if (originalRefreshToken) {
      localStorage.setItem('original_refresh_token', originalRefreshToken);
    }
    // Save original user snapshot so stopImpersonation can restore without HTTP call
    if (originalUserJson) {
      localStorage.setItem('original_user', originalUserJson);
    }

    localStorage.setItem(this.IMPERSONATION_KEY, JSON.stringify({
      companyName: data.companyName,
      companyId: data.companyId,
      originalCompanyId: data.originalCompanyId,
      expiresAt: data.expiresAt,
    }));

    localStorage.setItem('access_token', data.token);
    // Remove refresh token during impersonation so interceptor cannot silently
    // overwrite the impersonation token via the refresh cycle
    localStorage.removeItem('refresh_token');

    const currentUser = this.currentUserValue;
    if (currentUser) {
      const patchedUser = {
        ...currentUser,
        companyId: data.companyId,
        company: {
          ...(currentUser.company || {}),
          id: data.companyId,
          name: data.companyName,
          type: data.companyType ?? currentUser.company?.type ?? '',
          parentId: data.originalCompanyId,
        },
      };
      this.currentUserSubject.next(patchedUser);
      localStorage.setItem('currentUser', JSON.stringify(patchedUser));
    }

    // Hard reload into /dashboard - identity (company id + type) has changed, so
    // every already-instantiated component must re-initialise against the new
    // user. A soft router.navigate() reuses cached components (e.g. the payments
    // dashboard keeps its old main-company companyId=2 and keeps firing
    // main-only endpoints, which now 403 under the corporate impersonation token).
    window.location.assign('/dashboard');
  }

  stopImpersonation(): void {
    const originalToken = localStorage.getItem(this.ORIGINAL_TOKEN_KEY);
    if (!originalToken) return;

    // Restore original session tokens
    localStorage.setItem('access_token', originalToken);
    localStorage.removeItem(this.ORIGINAL_TOKEN_KEY);
    localStorage.removeItem(this.IMPERSONATION_KEY);

    const originalRefresh = localStorage.getItem('original_refresh_token');
    if (originalRefresh) {
      localStorage.setItem('refresh_token', originalRefresh);
      localStorage.removeItem('original_refresh_token');
    }

    // Restore original user from snapshot synchronously (no HTTP call needed)
    const originalUserJson = localStorage.getItem('original_user');
    if (originalUserJson) {
      try {
        const originalUser = JSON.parse(originalUserJson) as User;
        this.currentUserSubject.next(originalUser);
        this.isAuthenticatedSubject.next(true);
        localStorage.setItem('currentUser', JSON.stringify(originalUser));
      } catch {
        // Fallback: keep whatever is in currentUserSubject, do background refresh
      }
      localStorage.removeItem('original_user');
    }

    // Hard reload back into /dashboard - same reason as startImpersonation:
    // every component must re-initialise against the restored original user.
    window.location.assign('/dashboard');
  }

  getImpersonationData(): { companyName: string; companyId: number } | null {
    const raw = localStorage.getItem(this.IMPERSONATION_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  isImpersonating(): boolean {
    return !!localStorage.getItem(this.IMPERSONATION_KEY);
  }

  hasPermission(permission: string): boolean {
    const user = this.currentUserValue;
    if (!user || !user.role) {
      return false;
    }

    // Check if role has permissions (UserRole type)
    const roleWithPermissions = user.role as { permissions?: Array<{ name: string }> };
    if (!roleWithPermissions.permissions) {
      return false;
    }

    return roleWithPermissions.permissions.some(p => p.name === permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }
}
